// src/app/shared/roles.constants.ts
//
// Names of the roles this app's logic depends on. The registry itself is dbo.Roles, read
// through the roles API: which roles exist, what they are called on screen, and whether
// each is system-level or business-level all come from there — never from a list here.
// These constants must match dbo.Roles.Name exactly. Identity stores names lowercased
// (Yenni.Authorization.System.SystemRoles), so compare case-insensitively, never by
// reference.
//
// The seller role is MarketplaceAccountHolder, system-level — "MarketplaceSeller" is an
// old name for it that matches nothing, so never reintroduce it.

export const SYSTEM_ROLES = {
  Master: 'Master',
  PlatformAdmin: 'PlatformAdmin',
  ApplicationProcessor: 'ApplicationProcessor',
  WebsiteBuilder: 'WebsiteBuilder',
  /** Platform marketing staff. NOT 'Marketing', which is a merchant business role. */
  PlatformMarketing: 'PlatformMarketing',
  SuperAdmin: 'SuperAdmin',
  Support: 'Support',
  AccountSystemAdmin: 'AccountSystemAdmin',
  /** Paid Plus account owner. */
  AccountHolder: 'AccountHolder',
  /** Free-tier account owner: sells on the marketplace, no Plus subscription. */
  MarketplaceAccountHolder: 'MarketplaceAccountHolder',
  /** Staff member of a Plus business. */
  BusinessUser: 'BusinessUser',
  /** Marketplace customer: buys on the marketplace, owns no business, holds no plan. */
  MarketplaceBuyer: 'MarketplaceBuyer'
} as const;

/** The business-level role that makes a member a business's owner (dbo.Roles.Name). */
export const BUSINESS_OWNER_ROLE = 'BusinessOwner';

/**
 * Is this a system-level (global) role? dbo.Roles.IsSystemLevel decides — the same flag the
 * permission procedures and the API go by — so the admin shows roles exactly as the platform
 * treats them. A role assigned as a system role but flagged business-level is a data problem,
 * and the user page reports it as a profile issue rather than papering over it.
 */
export function isSystemRole(role: { isSystemLevel?: boolean | null }): boolean {
  return role?.isSystemLevel === true;
}

/**
 * The three populations the admin manages separately.
 *  - 'plus'       — Plus premium accounts: owners and their business staff.
 *  - 'marketplace'— marketplace sellers: the free tier's account-owner role.
 *  - 'customer'   — marketplace customer accounts.
 */
export type AccountType = 'plus' | 'marketplace' | 'customer';

/** Roles that make an account a Plus premium account, including platform staff. */
export const PLUS_ROLES: string[] = [
  SYSTEM_ROLES.AccountHolder,
  SYSTEM_ROLES.BusinessUser,
  SYSTEM_ROLES.AccountSystemAdmin,
  SYSTEM_ROLES.Master,
  SYSTEM_ROLES.PlatformAdmin,
  SYSTEM_ROLES.ApplicationProcessor,
  SYSTEM_ROLES.WebsiteBuilder,
  SYSTEM_ROLES.PlatformMarketing,
  SYSTEM_ROLES.SuperAdmin,
  SYSTEM_ROLES.Support
];

/** Roles that make an account a marketplace seller. */
export const MARKETPLACE_SELLER_ROLES: string[] = [SYSTEM_ROLES.MarketplaceAccountHolder];

/** The only role a marketplace customer account holds. */
export const MARKETPLACE_CUSTOMER_ROLES: string[] = [SYSTEM_ROLES.MarketplaceBuyer];

/** Roles a plan grants: AccountHolder for Plus, MarketplaceAccountHolder for sellers. */
export const OWNER_ROLES: string[] = [
  SYSTEM_ROLES.AccountHolder,
  SYSTEM_ROLES.MarketplaceAccountHolder
];

const lower = (roleNames: readonly string[]): string[] =>
  roleNames.map(r => (r || '').toLowerCase()).filter(r => !!r);

export function hasRole(roleNames: readonly string[], role: string): boolean {
  return lower(roleNames).includes(role.toLowerCase());
}

export function hasAnyRole(roleNames: readonly string[], roles: readonly string[]): boolean {
  const held = new Set(lower(roleNames));
  return roles.some(r => held.has(r.toLowerCase()));
}

/** Platform staff: they open this portal and approve at every merchant's till. */
export const PLATFORM_STAFF_ROLES: string[] = [SYSTEM_ROLES.SuperAdmin, SYSTEM_ROLES.Support];

export function isPlatformStaff(roleNames: readonly string[]): boolean {
  return hasAnyRole(roleNames, PLATFORM_STAFF_ROLES);
}

/**
 * May this admin change someone else's account - reset its password or PIN, switch its sign-in
 * off or on, give it a role at a business? Any admin may change an ordinary account; a platform
 * staff account only a SuperAdmin, or Support could make itself one. The same rule the API
 * enforces (SubscriptionManagement's StaffAccountRule); here it only hides buttons that would be
 * refused.
 */
export function mayChangeAccount(actorIsSuperAdmin: boolean, targetIsPlatformStaff: boolean): boolean {
  return actorIsSuperAdmin || !targetIsPlatformStaff;
}

/**
 * Roles that must sign in with an authenticator app (the identity server's
 * PlatformStaffSecurity:Roles). A different question from PLATFORM_STAFF_ROLES, which decides
 * who may change whom: ApplicationProcessor needs an authenticator but is not protected staff,
 * and the two lists are kept apart on purpose. Used only to drive the Users filter, the counts,
 * the Authenticator panel and the sign-ins view - never a who-may-change-whom check.
 */
export const MFA_REQUIRED_ROLES: string[] = [
  SYSTEM_ROLES.SuperAdmin,
  SYSTEM_ROLES.Support,
  SYSTEM_ROLES.ApplicationProcessor
];

export function requiresAuthenticator(roleNames: readonly string[]): boolean {
  return hasAnyRole(roleNames, MFA_REQUIRED_ROLES);
}

/**
 * Does this account belong on the given page? An account holding roles from more
 * than one population (a seller who also shops, say) appears on each page it
 * qualifies for — the page filters, it does not claim exclusive ownership.
 *
 * Accounts with no roles at all show under Plus: a signup that never finished has
 * no role yet, and must stay visible somewhere rather than vanish from every list.
 */
export function belongsTo(roleNames: readonly string[], accountType: AccountType): boolean {
  switch (accountType) {
    case 'marketplace':
      return hasAnyRole(roleNames, MARKETPLACE_SELLER_ROLES);
    case 'customer':
      return hasAnyRole(roleNames, MARKETPLACE_CUSTOMER_ROLES);
    default:
      return roleNames.length === 0 || hasAnyRole(roleNames, PLUS_ROLES);
  }
}

/**
 * Should this system role be offered when creating an account on the given page?
 * The seller and customer pages offer only their own role; Plus is the catch-all, so
 * any other system role (including ones added later) stays available there.
 */
export function isRoleOfferedOn(roleName: string, accountType: AccountType): boolean {
  if (!roleName) return false;
  const isSeller = hasAnyRole([roleName], MARKETPLACE_SELLER_ROLES);
  const isCustomer = hasAnyRole([roleName], MARKETPLACE_CUSTOMER_ROLES);
  switch (accountType) {
    case 'marketplace': return isSeller;
    case 'customer': return isCustomer;
    default: return !isSeller && !isCustomer;
  }
}

/** True when the account is only ever a marketplace customer — owns nothing on the platform. */
export function isCustomerOnly(roleNames: readonly string[]): boolean {
  return hasAnyRole(roleNames, MARKETPLACE_CUSTOMER_ROLES)
    && !hasAnyRole(roleNames, PLUS_ROLES)
    && !hasAnyRole(roleNames, MARKETPLACE_SELLER_ROLES);
}

/** Short label for the list, so the kind of account is visible without opening it. */
export function accountRoleLabel(roleNames: readonly string[]): string {
  if (hasRole(roleNames, SYSTEM_ROLES.Master)) return 'Master';
  if (hasRole(roleNames, SYSTEM_ROLES.SuperAdmin)) return 'Super admin';
  if (hasRole(roleNames, SYSTEM_ROLES.PlatformAdmin)) return 'Admin';
  if (hasRole(roleNames, SYSTEM_ROLES.Support)) return 'Support';
  if (hasRole(roleNames, SYSTEM_ROLES.ApplicationProcessor)) return 'Application processor';
  if (hasRole(roleNames, SYSTEM_ROLES.WebsiteBuilder)) return 'Website builder';
  if (hasRole(roleNames, SYSTEM_ROLES.PlatformMarketing)) return 'Platform marketing';
  if (hasRole(roleNames, SYSTEM_ROLES.AccountHolder)) return 'Account holder';
  if (hasRole(roleNames, SYSTEM_ROLES.MarketplaceAccountHolder)) return 'Seller';
  if (hasRole(roleNames, SYSTEM_ROLES.AccountSystemAdmin)) return 'System admin';
  if (hasRole(roleNames, SYSTEM_ROLES.BusinessUser)) return 'Business user';
  if (hasRole(roleNames, SYSTEM_ROLES.MarketplaceBuyer)) return 'Customer';
  return 'No system role';
}
